import * as toast from '@weblite/react-toastify'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { RoomTypes } from '@weblite/ts-action/room'
import { CallType } from '@weblite/ts-action/media-proxy'
import { nothing } from '$func/utils'
import { RootState } from '$setup/redux'
import {
  callEndedAudio,
  incomingCallAudio,
  outgoingCallAudio,
} from '$setup/audios'
import { useTimeout } from '$hooks/use-timeout'
import { showErrorSnackbar } from '$comp/snackbars'
import { t } from '$i18n'
import { callService } from '$logic/call/service'
import { useLastCallback } from '$hooks/use-last-callback'
import { CallStorage } from './storage'
import useEffectWithPrevDeps from '$hooks/use-effect-with-prev-deps'
import {
  CallAction,
  reportCallActionFailure,
} from '$logic/analytics/reporters/call-action-failed'

const callStorageSelector = (rootState: RootState) =>
  rootState.main.call as CallStorage

const callPvMetaSelector = (rootState: RootState) =>
  (rootState.main.call as CallStorage).pvMeta

const callHallMetaSelector = (rootState: RootState) =>
  (rootState.main.call as CallStorage).hallMeta

const callRecordModeSelector = (rootState: RootState) =>
  (rootState.main.call as CallStorage).recordMode

export function useCallStorage() {
  return useSelector(callStorageSelector)
}

export function useCallStoragePvMeta() {
  return useSelector(callPvMetaSelector)
}

export function useCallStorageHallMeta() {
  return useSelector(callHallMetaSelector)
}
export function useIsRecordingCall() {
  return useSelector(callRecordModeSelector)
}

export function useShowCallBar() {
  const {
    roomId: ongoingCallRoomId,
    pvMeta: { roomId: pvCallRoomId, state: pvCallState },
  } = useCallStorage()

  const hasIncomingOrOngoingPvCall = Boolean(
    pvCallRoomId && pvCallState !== 'ended',
  )

  return Boolean(ongoingCallRoomId) || hasIncomingOrOngoingPvCall
}

export function useHandleCallSounds() {
  const { state, status } = useCallStoragePvMeta()

  useEffectWithPrevDeps(
    ([oldState]) => {
      if (state === oldState) return

      // state !== oldState
      outgoingCallAudio.pause()
      incomingCallAudio.pause()

      if (state === 'ended' && status !== 'successful')
        callEndedAudio.play()?.catch(nothing)
      else if (state === 'outgoing') outgoingCallAudio.play()?.catch(nothing)
      else if (state === 'incoming') incomingCallAudio.play()?.catch(nothing)
    },
    [state, status],
  )
}

const JOIN_CALL_TIMEOUT = 10000
const JOIN_CALL_TIMEOUT_SNACKBAR_ID = 'joinCallTimeout'
export function useJoinCallWithTimeout(timeoutMs = JOIN_CALL_TIMEOUT) {
  const [joiningCall, setJoiningCall] = useState(false)
  const { create: createTimeout, clear: cancelTimeout } = useTimeout()

  const joinCall = useLastCallback(
    (roomId: string, roomType: RoomTypes, callType: CallType) => {
      createTimeout(() => {
        setJoiningCall(false)
        showErrorSnackbar(
          t('snackbar.call.joinFailed')(t(`call.${callType}`)),
          JOIN_CALL_TIMEOUT_SNACKBAR_ID,
        )

        reportCallActionFailure(CallAction.JOIN_CALL, callType, timeoutMs, {
          roomId,
          roomType,
        })
      }, timeoutMs)

      setJoiningCall(true)
      callService.joinCall(roomId, roomType, callType).finally(() => {
        setJoiningCall(false)
        toast.dismiss(JOIN_CALL_TIMEOUT_SNACKBAR_ID)
        cancelTimeout()
      })
    },
  )

  return { joiningCall, joinCall }
}
